// Side-panel layer: renders every visible tab from the store and handles the
// delegated data-pl2 events the panels emit. `ctx` is the composition root
// (the Stimulus controller) exposing: store, editor, viewOnlyValue,
// ownerValue/descriptionValue, initLayerSort, startPlacement, focusElements,
// openSpeciesSearch, addSpeciesToSelected, selectedElement, loadMoreSearch,
// searchHit(i), listPlant(listId, i).

import { layersPanelHtml } from './layers';
import { elementsPanelHtml } from './elements';
import { plantsPanelHtml, plantElements } from './plants';
import { listsPanelHtml } from './lists';
import { propsPanelHtml, isSectionCollapsed } from './props';
import { sliderToWidth, widthToDisplay, widthFromDisplay, DEFAULT_WIDTH } from './appearance';
import { patchDrawStyle } from '../model/draw_style';
import { normalizeDate } from '../dates';
import { unlockedIds } from '../model/mutators/elements';
import { infoPanelHtml } from './info';
import { updateLayer, addLayer, deleteLayer } from '../model/mutators/layers';
import { speciesKeyOptions } from '../model/labels';
import { updateLabel, setLabelKeys, moveLabel, removeLabel, addLabel, removeSpecies, addSpeciesToElements } from '../model/mutators/labels';
import { setElementProps, imageSizePatch } from '../model/mutators/props';
import { setSideLength, setSegmentType } from '../model/mutators/paths';
import { toggleLock } from '../model/mutators/elements';
import { planSideLength } from '../path_sides';
import { hexToRgba } from './html';

export function renderPanels(root, state, ctx) {
  const panel = (name) => root.querySelector(`[data-tab-panel="${name}"]`);
  const filters = ctx.filters || {};

  const layers = panel('layers');
  if (layers) {
    layers.innerHTML = layersPanelHtml(state, { viewOnly: ctx.viewOnlyValue });
    if (!ctx.viewOnlyValue) ctx.initLayerSort(layers.querySelector('.pl2-layer-list'));
  }

  const elements = panel('elements');
  if (elements) elements.innerHTML = elementsPanelHtml(state, { filter: filters.elements || '', layer: filters.elementsLayer || '', viewOnly: ctx.viewOnlyValue });

  const plants = panel('plants');
  if (plants) plants.innerHTML = plantsPanelHtml(state, { filter: filters.plants || '', layer: filters.plantsLayer || '' });

  const props = panel('properties');
  if (props) {
    props.innerHTML = propsPanelHtml(state, { imageSize: (el) => ctx.imageSize?.(el) ?? null, collapsed: ctx.collapsed || {}, labelEditing: ctx.labelEditing || null });
    if (!ctx.viewOnlyValue) ctx.initLabelSort?.(props);
    // A custom label's pencil (or + Add label) opens its text input, focused.
    const text = props.querySelector('[data-pl2="label-text"]');
    if (text && ctx._focusLabel) { text.focus(); text.select(); }
    ctx._focusLabel = false;
  }

  const info = panel('info');
  if (info) info.innerHTML = infoPanelHtml(state, { owner: ctx.ownerValue, description: ctx.descriptionValue, collapsed: ctx.collapsed || {} });

  const lists = panel('lists');
  if (lists) lists.innerHTML = listsPanelHtml({ username: ctx.usernameValue, loading: ctx._listsLoading, lists: ctx._lists });
}

// Live filtering as you type. The panel is rebuilt wholesale, so the caret has
// to be put back — otherwise the field loses focus on the first keystroke.
export function handlePanelInput(e, ctx) {
  const kind = e.target.dataset.pl2;
  if (!ctx.viewOnlyValue && LIVE_KINDS.has(kind)) { previewLive(e, ctx); return; }
  const key = { 'filter-elements': 'elements', 'filter-plants': 'plants' }[kind];
  if (!key) return;
  ctx.filters = { ...(ctx.filters || {}), [key]: e.target.value };
  const caret = e.target.selectionStart;
  const panelName = e.target.closest('[data-tab-panel]')?.dataset.tabPanel;
  ctx.refreshPanels();
  // Re-query the live DOM: the input the event came from is detached now.
  const again = ctx.element.querySelector(`[data-tab-panel="${panelName}"] [data-pl2="${kind}"]`);
  if (again) { again.focus(); try { again.setSelectionRange(caret, caret); } catch (_) { /* search inputs */ } }
}

// --- live previews -----------------------------------------------------------
// Colour pickers and sliders update as they move. A store update would
// re-render the panel and swap the input out from under the pointer (closing a
// colour picker), so a preview mutates the state in place and redraws only the
// canvas. The snapshot taken before the first tick lets the release (the
// input's `change`) commit the whole gesture as one undo step.

const LIVE_KINDS = new Set(['layer-opacity', 'prop-fill', 'prop-stroke', 'prop-strokeWidthSlider', 'prop-opacity']);

// The data patch a live input stands for (the picker/slider value), or null.
function livePatch(kind, value, prev = {}) {
  if (kind === 'prop-fill') return { fillColor: hexToRgba(value, prev.fillColor) };
  if (kind === 'prop-stroke') return { strokeColor: hexToRgba(value, prev.strokeColor) };
  if (kind === 'prop-strokeWidthSlider') return { strokeWidth: sliderToWidth(Number(value)) };
  if (kind === 'prop-opacity') return { opacity: Number(value) };
  return null;
}

function previewLive(e, ctx) {
  const kind = e.target.dataset.pl2;
  const { store } = ctx;
  // The slider drives the thickness box next to it, without a re-render.
  if (kind === 'prop-strokeWidthSlider') {
    const box = e.target.closest('.pl2-props-section__body')?.querySelector('[data-pl2="prop-strokeWidth"]');
    if (box) box.value = widthToDisplay(sliderToWidth(Number(e.target.value)), store.state.settings.units);
  }
  const draw = drawTarget(e);
  if (draw) { // the next shape's style: session state, no undo, nothing to redraw
    Object.assign(store.state.drawStyles[draw], livePatch(kind, e.target.value, store.state.drawStyles[draw]));
    return;
  }
  if (!ctx._livePrev) ctx._livePrev = ctx.editor.takeSnapshot();
  if (kind === 'layer-opacity') {
    const layer = store.layerById(e.target.dataset.id);
    if (layer) layer.opacity = parseFloat(e.target.value);
    scheduleRedraw(ctx, null);
  } else {
    const els = selectedEditable(store.state);
    for (const el of els) Object.assign(el.data, livePatch(kind, e.target.value, el.data));
    scheduleRedraw(ctx, els.map((el) => el.id));
  }
}

// A colour picker fires `input` many times a second while its dot is dragged.
// The state updates on every one (cheap), but the canvas redraws at most once
// per frame — just the changed elements (`ids`), or everything (`null`, e.g. a
// layer's opacity).
function scheduleRedraw(ctx, ids) {
  if (ids === null) ctx._liveAll = true;
  else ctx._liveIds = new Set([...(ctx._liveIds || []), ...ids]);
  if (ctx._liveFrame) return;
  const frame = ctx.scheduleFrame || ((fn) => requestAnimationFrame(fn));
  ctx._liveFrame = true;
  frame(() => {
    ctx._liveFrame = false;
    const all = ctx._liveAll; const redraw = [...(ctx._liveIds || [])];
    ctx._liveAll = false; ctx._liveIds = null;
    if (all) ctx.renderer?.reconcile(ctx.store.state, { source: 'preview' });
    else ctx.renderer?.restyle(redraw);
  });
}

// Finish a live gesture as one undo step; without a preceding preview (e.g. a
// keyboard change) it's an ordinary edit through `fallback`.
function commitLive(ctx, mutate, fallback) {
  const prev = ctx._livePrev; ctx._livePrev = null;
  if (!prev) { fallback(); return; }
  mutate();
  ctx.editor.commitGesture(prev);
}

function selectedEditable(state) {
  const ids = new Set(unlockedIds(state, state.selection));
  return state.elements.filter((el) => ids.has(el.id));
}

// Inputs in the draw-mode view edit the draw tool's style, not an element.
function drawTarget(e) {
  return e.target.closest?.('[data-props-target="draw"]')?.dataset.tool || null;
}

// The value of a sibling field in the same Properties section.
function sectionField(e, kind) {
  return e.target.closest('.pl2-props-section__body')?.querySelector(`[data-pl2="${kind}"]`);
}

// On/off toggles and the thickness box (shown in m/ft) -> a data patch.
function appearancePatch(e, kind, units, prev = {}) {
  const v = e.target.value;
  if (kind === 'prop-use-fill') return { fillColor: e.target.checked ? hexToRgba(sectionField(e, 'prop-fill')?.value || '#72b45f', prev.fillColor) : null };
  if (kind === 'prop-use-stroke') return { strokeColor: e.target.checked ? hexToRgba(sectionField(e, 'prop-stroke')?.value || '#000000', prev.strokeColor) : null };
  if (kind === 'prop-use-width') {
    const shown = Number(sectionField(e, 'prop-strokeWidth')?.value);
    return { strokeWidth: e.target.checked ? (shown > 0 ? widthFromDisplay(shown, units) : DEFAULT_WIDTH) : null };
  }
  if (kind === 'prop-strokeWidth') {
    const n = Number(v);
    return v.trim() === '' || !(n > 0) ? null : { strokeWidth: widthFromDisplay(n, units) };
  }
  return null;
}

// Layer and date fields -> a data patch; null for other fields, false for a
// date that isn't one (the field is flagged and nothing changes).
function sharedPatch(e, kind) {
  if (kind === 'prop-layer') return { layer: e.target.value || null };
  if (kind !== 'prop-addedDate' && kind !== 'prop-removedDate') return null;
  const date = normalizeDate(e.target.value);
  const invalid = date === undefined;
  e.target.classList.toggle('is-invalid', invalid);
  e.target.setAttribute('aria-invalid', invalid ? 'true' : 'false');
  if (invalid) return false;
  return { [kind === 'prop-addedDate' ? 'addedDate' : 'removedDate']: date };
}

// Draw-mode view: the next shape's appearance and dates, straight into the
// session draw styles (no undo — nothing on the plan changed).
function handleDrawChange(e, ctx, tool) {
  const kind = e.target.dataset.pl2;
  const { store } = ctx;
  const styles = store.state.drawStyles;
  const v = e.target.value;
  const shared = sharedPatch(e, kind);
  if (shared === false) return; // an invalid date, flagged on the field
  if (shared) { store.set({ drawStyles: patchDrawStyle(styles, 'shared', shared) }); return; }
  let patch = appearancePatch(e, kind, store.state.settings.units, styles[tool])
    ?? livePatch(kind, v, styles[tool]);
  if (kind === 'prop-canopy') patch = { canopyStyle: v };
  if (kind === 'prop-segmentType') patch = { segmentType: v };
  if (patch) store.set({ drawStyles: patchDrawStyle(styles, tool, patch) });
}

// change events (inputs/selects/checkboxes)
export function handlePanelChange(e, ctx) {
  const kind = e.target.dataset.pl2;
  if (!kind) return;
  if (kind === 'filter-elements' || kind === 'filter-plants') return; // handled on input
  const layerKey = { 'filter-elements-layer': 'elementsLayer', 'filter-plants-layer': 'plantsLayer' }[kind];
  if (layerKey) {
    ctx.filters = { ...(ctx.filters || {}), [layerKey]: e.target.value };
    ctx.refreshPanels();
    return;
  }
  const { editor, store } = ctx;

  if (kind === 'layer-visible') {
    // Viewers can hide layers to explore, but nothing is saved (or dirty).
    if (ctx.viewOnlyValue) {
      const id = e.target.dataset.id; const visible = e.target.checked;
      store.set({ layers: store.state.layers.map((l) => (l.id === id ? { ...l, visible } : l)) });
      return;
    }
    editor.apply(updateLayer, { id: e.target.dataset.id, patch: { visible: e.target.checked } });
    return;
  }
  if (ctx.viewOnlyValue) return;
  const tool = drawTarget(e);
  if (tool) { handleDrawChange(e, ctx, tool); return; }
  if (kind === 'layer-opacity') {
    const patch = { opacity: parseFloat(e.target.value) };
    commitLive(ctx, () => {
      const layer = store.layerById(e.target.dataset.id);
      if (layer) Object.assign(layer, patch);
    }, () => editor.apply(updateLayer, { id: e.target.dataset.id, patch }));
    return;
  }
  if (kind === 'layer-rename') { editor.apply(updateLayer, { id: e.target.dataset.id, patch: { name: e.target.value } }); return; }

  // prop-* fields apply to the whole selection (single-element panel and the
  // mass-edit panel share the same handler — one mutator call, one undo step).
  const sel = store.state.selection;
  if (!sel.length) return;
  const first = store.state.elements.find((x) => x.id === sel[0]);
  if (!first) return;
  const v = e.target.value;

  // Sizes are typed in m / ft. A single image resizes with its aspect ratio
  // unless deliberately unlocked.
  if (kind === 'prop-width' || kind === 'prop-height') {
    const size = Number(v);
    if (v.trim() === '' || !Number.isFinite(size) || size <= 0) return;
    const cm = widthFromDisplay(size, store.state.settings.units);
    const field = kind === 'prop-width' ? 'width' : 'height';
    const patch = sel.length === 1 && first.type === 'image'
      ? imageSizePatch(first, field, cm, ctx.imageSize?.(first))
      : { [field]: cm };
    editor.apply(setElementProps, { ids: sel, patch });
    return;
  }

  if (kind === 'prop-segmentType') {
    const el = ctx.selectedElement();
    if (el) editor.apply(setSegmentType, { id: el.id, index: Number(e.target.dataset.index), type: v });
    return;
  }

  if (kind === 'prop-side') {
    const el = ctx.selectedElement();
    const length = Number(v.trim());
    if (!el || v.trim() === '' || !Number.isFinite(length) || length < 0) return;
    editor.apply(setSideLength, {
      id: el.id, side: Number(e.target.dataset.i), length: planSideLength(length, store.state.settings.units),
    });
    return;
  }
  // Colours and sliders were previewed live; this `change` ends the gesture.
  const live = livePatch(kind, v, first.data);
  if (live) {
    commitLive(ctx, () => { for (const el of selectedEditable(store.state)) Object.assign(el.data, live); },
      () => editor.apply(setElementProps, { ids: sel, patch: live }));
    return;
  }
  const appearance = appearancePatch(e, kind, store.state.settings.units, first.data);
  if (appearance) { editor.apply(setElementProps, { ids: sel, patch: appearance }); return; }
  const shared = sharedPatch(e, kind);
  if (shared === false) return;
  if (shared) { editor.apply(setElementProps, { ids: sel, patch: shared }); return; }
  if (kind === 'prop-strokeWidth') return; // an empty/invalid thickness: ignore
  const propPatch = {
    'prop-rotation': () => ({ rotation: Number(v) * Math.PI / 180 }),
    'prop-notes': () => ({ notes: v || null }),
    'prop-canopy': () => ({ canopyStyle: v }),
    'prop-image-name': () => ({ name: v }),
    'prop-visible': () => ({ visible: e.target.checked }),
  }[kind];
  if (propPatch) {
    editor.apply(setElementProps, { ids: sel, patch: propPatch(), canopySeed: Math.floor(Math.random() * 100000) });
    return;
  }

  // Label edits stay single-selection (they index into one element's labels).
  const el = ctx.selectedElement();
  if (!el) return;
  const i = e.target.dataset.i != null ? Number(e.target.dataset.i) : null;
  // The panel is rebuilt on every selection change, so a `change` can fire on a
  // label input that belonged to a now-deselected element (click-away while
  // editing). Drop it when the input's element no longer matches, and skip an
  // out-of-range index, rather than write to the wrong element.
  if (e.target.dataset.elId !== el.id || i == null || i < 0 || i >= (el.data.labels || []).length) return;
  if (kind === 'label-visible') editor.apply(updateLabel, { elementId: el.id, index: i, patch: { visible: e.target.checked } });
  else if (kind === 'label-text') {
    // Committing the text ends the edit (see handlePanelFocusOut for _labelClosed).
    ctx._labelClosed = pressedOwnPencil(e, ctx, el.id, i) ? { elId: el.id, i, at: Date.now() } : null;
    ctx.labelEditing = null;
    editor.apply(updateLabel, { elementId: el.id, index: i, patch: { value: v } });
  } else if (kind === 'label-key') {
    editor.apply(setLabelKeys, { elementId: el.id, index: i, keys: checkedKeys(e.target.closest('.pl2-keyoptions')) });
  }
}

// A species label's shown keys: the ticked options, top to bottom.
export function checkedKeys(list) {
  if (!list) return [];
  return [...list.querySelectorAll('[data-pl2="label-key"]')].filter((c) => c.checked).map((c) => c.dataset.key);
}

// Enter commits a label's text, Esc drops the edit.
export function handlePanelKeydown(e, ctx) {
  if (e.target.dataset?.pl2 !== 'label-text') return;
  if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
  else if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    ctx.labelEditing = null;
    ctx.refreshPanels();
  }
}

// Whether the press that ended a label edit was on that label's own pencil
// (✓) — that click then closes the edit instead of reopening it. Ended any
// other way (Enter, a click elsewhere), the pencil reopens it straight away.
function pressedOwnPencil(e, ctx, elId, i) {
  const pencil = (e.relatedTarget || ctx._pressTarget)?.closest?.('[data-pl2="label-edit"]');
  return !!pencil && pencil.dataset.elId === elId && Number(pencil.dataset.i) === i;
}

// Leaving the text input unchanged (no `change`) also ends the edit.
export function handlePanelFocusOut(e, ctx) {
  if (e.target.dataset?.pl2 !== 'label-text' || !ctx.labelEditing) return;
  // Clicking the pencil (✓) is what moved focus: let that click close it,
  // rather than close here and have the click reopen it.
  const editing = ctx.labelEditing;
  ctx._labelClosed = pressedOwnPencil(e, ctx, editing.elId, editing.i) ? { ...editing, at: Date.now() } : null;
  ctx.labelEditing = null;
  // After the `change` (if any) and the click that moved focus have run.
  setTimeout(() => ctx.refreshPanels(), 0);
}

// Drag-reorder a label row (`from` -> `to`) or a species label's options.
export function reorderLabels(ctx, elementId, from, to) {
  const el = ctx.store.state.elements.find((x) => x.id === elementId);
  if (!el || from === to) return;
  ctx.labelEditing = null;
  ctx.editor.apply(moveLabel, { elementId, from, to });
}

export function reorderLabelKeys(ctx, list) {
  const elementId = list.dataset.elId;
  const index = Number(list.dataset.i);
  if (!ctx.store.state.elements.some((x) => x.id === elementId)) return;
  const order = [...list.querySelectorAll('.pl2-keyoption')].map((o) => o.dataset.key);
  const label = ctx.store.state.elements.find((x) => x.id === elementId).data.labels?.[index];
  if (label) ctx.keyOrders = { ...(ctx.keyOrders || {}), [`${elementId}:${label.id}`]: order };
  if (ctx.labelEditing) ctx.labelEditing = { ...ctx.labelEditing, order };
  ctx.editor.apply(setLabelKeys, { elementId, index, keys: checkedKeys(list) });
}

// click events (rows/buttons). Real links (data-profile) pass through untouched.
export function handlePanelClick(e, ctx) {
  if (e.target.closest?.('[data-profile]')) return;
  const target = e.target.closest('[data-pl2]');
  if (!target) return;
  const kind = target.dataset.pl2;
  const { editor, store } = ctx;

  // View mode also brings the selection into view when it's off-screen.
  // Locked elements can't be selected while editing (view mode still can —
  // it only reads them).
  const select = (ids) => {
    if (ctx.viewOnlyValue) { ctx.focusElements(ids); return; }
    const locked = new Set(store.state.elements.filter((el) => el.data?.locked).map((el) => el.id));
    store.set({ selection: ids.filter((id) => !locked.has(id)) });
  };
  if (kind === 'prop-lockRatio') {
    const el = ctx.selectedElement();
    if (el?.type === 'image' && !ctx.viewOnlyValue) editor.apply(setElementProps, { ids: [el.id], patch: { lockRatio: el.data?.lockRatio === false } });
    return;
  }
  if (kind === 'segment-pick') {
    const el = ctx.selectedElement();
    if (!el || ctx.viewOnlyValue || el.data?.locked) return;
    const index = Number(target.dataset.index);
    const same = store.state.selectedSegment?.id === el.id && store.state.selectedSegment.index === index;
    store.set({ selectedSegment: same ? null : { id: el.id, index } });
    return;
  }
  if (kind === 'element-lock') {
    if (!ctx.viewOnlyValue) editor.apply(toggleLock, { id: target.dataset.id });
    return;
  }
  if (kind === 'element-select') {
    if (!ctx.viewOnlyValue && store.state.elements.find((el) => el.id === target.dataset.id)?.data?.locked) return;
    select([target.dataset.id]);
    return;
  }
  if (kind === 'plant-select') {
    const sid = Number(target.dataset.id);
    const ids = plantElements(store.state, ctx.filters?.plantsLayer)
      .filter((el) => (el.speciesIds || []).includes(sid)).map((el) => el.id);
    select(ids);
    return;
  }
  if (kind === 'clear-selection') { store.set({ selection: [] }); return; }
  if (kind === 'toggle-section') {
    const key = target.dataset.section;
    ctx.collapsed = { ...(ctx.collapsed || {}), [`props-${key}`]: !isSectionCollapsed(ctx.collapsed, key) };
    ctx.refreshPanels();
    return;
  }
  if (kind === 'toggle-dimensions') {
    ctx.collapsed = { ...(ctx.collapsed || {}), dimensions: !ctx.collapsed?.dimensions };
    ctx.refreshPanels();
    return;
  }
  if (ctx.viewOnlyValue) return;

  if (kind === 'layer-delete') editor.apply(deleteLayer, { id: target.dataset.id });
  else if (kind === 'layer-add') editor.apply(addLayer, { id: `layer-${Date.now()}` });
  else if (kind === 'place') {
    const h = ctx.searchHit(Number(target.dataset.idx));
    if (h) ctx.startPlacement(h);
  } else if (kind === 'search-add-sel') {
    const h = ctx.searchHit(Number(target.dataset.idx));
    if (!h) return;
    if (!store.state.selection.length) { ctx.flashHint?.('Select one or more elements first, then click ＋ to add the plant to them.'); return; }
    editor.apply(addSpeciesToElements, { ids: store.state.selection, plant: h });
  } else if (kind === 'search-add-el') {
    const h = ctx.searchHit(Number(target.dataset.idx));
    if (h) ctx.startAddSpecies(h);
  } else if (kind === 'search-more') {
    ctx.loadMoreSearch();
  } else if (kind === 'list-place') {
    const plant = ctx.listPlant(target.dataset.list, Number(target.dataset.idx));
    if (plant) ctx.startPlacement(plant);
  } else if (kind === 'label-edit') {
    const el = ctx.selectedElement();
    const i = Number(target.dataset.i);
    if (!el || target.dataset.elId !== el.id) return;
    const closed = ctx._labelClosed;
    const justClosed = closed?.elId === el.id && closed.i === i && Date.now() - closed.at < 500;
    const open = justClosed || (ctx.labelEditing?.elId === el.id && ctx.labelEditing.i === i);
    ctx._labelClosed = null;
    const label = el.data.labels?.[i];
    // The option order is pinned per element + plant for the page session, so
    // an option ticked off stays where it was, even after closing and reopening.
    const orderKey = label?.type === 'species' ? `${el.id}:${label.id}` : null;
    const order = orderKey ? speciesKeyOptions(store.state.species[label.id], label.keys || [], ctx.keyOrders?.[orderKey]) : null;
    if (orderKey) ctx.keyOrders = { ...(ctx.keyOrders || {}), [orderKey]: order };
    ctx.labelEditing = open ? null : { elId: el.id, i, order };
    ctx._focusLabel = !open;
    ctx.refreshPanels();
  } else if (kind === 'label-remove') {
    const el = ctx.selectedElement();
    if (!el) return;
    ctx.labelEditing = null;
    editor.apply(removeLabel, { elementId: el.id, index: Number(target.dataset.i) });
  } else if (kind === 'label-add') {
    const el = ctx.selectedElement();
    if (!el) return;
    // Opens straight into editing, text selected, so typing replaces it.
    ctx.labelEditing = { elId: el.id, i: (el.data.labels || []).length };
    ctx._focusLabel = true;
    editor.apply(addLabel, { elementId: el.id, label: { type: 'custom', value: 'Label', visible: true } });
  } else if (kind === 'species-remove') {
    const el = ctx.selectedElement();
    if (el) ctx.labelEditing = null;
    if (el) editor.apply(removeSpecies, { elementId: el.id, speciesId: Number(target.dataset.id) });
  } else if (kind === 'species-add') {
    ctx.openSpeciesSearch((plant) => ctx.addSpeciesToSelected(plant));
  }
}
